import { useDeferredValue, useState, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { FilterManager } from '@app/managers/filters'
import Papa from 'papaparse'
import { downloadFile } from '@app/lib/download-csv'
import { FeedButton } from './feed-item-button'
import type { FeedItemProps } from './feed-item-types'
import { FeedInnerList } from './feed-inner-list'
import { Issue } from '@app/types'

const FeedItemWrapper = ({
  feed,
  onScanEvent,
  index,
  domain,
  hideSync,
  demo,
}: FeedItemProps) => {
  const [visible, setVisible] = useState<boolean>(index === 0)
  const [sorted, setSorted] = useState<boolean>(false)
  const [grouped, setGrouped] = useState<boolean>(false)

  // data for the webpage
  const pages = useDeferredValue(feed?.get_website_keys(domain) ?? [])

  const onHeadingToggleEvent = useCallback(
    () => setVisible((v) => !v),
    [setVisible]
  )

  const onSortClick = useCallback(() => {
    if (domain && feed) {
      feed.sort_website(domain)
      if (grouped) {
        FilterManager.sortGroups()
      }
    }
    setSorted((s) => !s)
  }, [setSorted, feed, domain, grouped])

  // render the group items block list
  const onGroupClickEvent = useCallback(() => {
    setGrouped((x) => !x)
    FilterManager.toggleGrouping()
  }, [])

  const onCSVClick = useCallback(
    (format?: string) => {
      const data = feed.get_website(domain) as Map<string, any>
      const parseData = new Array(data.size)

      let i = 0

      // TODO: streaming
      const cb =
        typeof queueMicrotask === 'function' ? queueMicrotask : setTimeout

      const has_filters = FilterManager.filterList.length

      if (format === 'json') {
        if (has_filters) {
          for (const [_, value] of data) {
            cb(() => {
              const filtered =
                value &&
                FilterManager.filters.has(value?.code) &&
                FilterManager.filters.get(value?.code)?.checked

              if (!filtered) {
                parseData[i] = value
                i++
              }
            })
          }
        } else {
          for (const [_, value] of data) {
            cb(() => {
              parseData[i] = value
              i++
            })
          }
        }

        parseData.length = i

        cb(() => {
          downloadFile(JSON.stringify(parseData), `${domain}-issues.json`)
        })
      } else {
        for (const [_, value] of data) {
          cb(() => {
            let st = ''

            if (value.issues && value.issues.length) {
              try {
                st = JSON.stringify(
                  has_filters
                    ? value.issues?.filter((item: Issue) => {
                        const filtered =
                          item &&
                          FilterManager.filters.has(item?.code) &&
                          FilterManager.filters.get(item?.code)?.checked

                        return !filtered
                      })
                    : value.issues
                )
              } catch (e) {
                console.error(e)
              }
            }

            const { errorCount, warningCount } = value.issuesInfo ?? {}

            parseData[i] = {
              DOMAIN: value.domain || '',
              URL: value.pageUrl || '',
              // SCORE: accessScore,
              ERRORS: errorCount || 0,
              WARNINGS: warningCount || 0,
              ISSUES: st,
            }

            i++
          })
        }

        cb(() => {
          parseData.length = i
          const d = Papa.unparse(parseData)
          downloadFile(d, `${domain}-issues.csv`)
        })
      }
    },
    [feed, domain]
  )

  const onJsonClick = useCallback(() => {
    onCSVClick('json')
  }, [onCSVClick])

  if (!pages.length) {
    return null
  }

  return (
    <li>
      <FeedButton
        domain={domain}
        pageCount={pages.length}
        grouped={grouped}
        onHeadingToggleEvent={onHeadingToggleEvent}
        onSortClick={onSortClick}
        onGroupClick={onGroupClickEvent}
        onCSVClick={onCSVClick}
        onJsonClick={onJsonClick}
      />
      <FeedInnerList
        hideSync={hideSync}
        grouped={grouped}
        visible={visible}
        onScanEvent={onScanEvent}
        sorted={sorted}
        feed={feed}
        domain={domain}
        pages={pages}
        demo={demo}
      />
    </li>
  )
}

export const FeedItem = observer(FeedItemWrapper)
